import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import {
  LayoutDashboard, Users, UserCircle, FileText, Settings, BarChart3,
  ClipboardList, MessageSquare, LogOut, Building2, BanknoteIcon,
  Calendar, IndianRupee, Download, Moon, Sun, Globe, ChevronDown, ChevronRight,
  Receipt, CreditCard, Wallet, ShieldAlert, TrendingUp, BookOpen,
  Upload, Lock, GraduationCap, Shield, PauseCircle, SplitSquareHorizontal,
  Banknote, FileSpreadsheet, ClipboardCheck, Trophy, Award, Fingerprint, Briefcase,
} from "lucide-react";

const navKeys = {
  superadmin: [
    { key: "nav.dashboard", icon: LayoutDashboard, path: "/app/dashboard" },
    { key: "nav.users", icon: Users, path: "/app/users" },
    { key: "nav.customers", icon: UserCircle, path: "/app/customers" },
    { key: "nav.proposals", icon: ClipboardList, path: "/app/proposals" },
    { key: "nav.disbursements", icon: BanknoteIcon, path: "/app/disbursements" },
    { key: "nav.paymentVerification", icon: Wallet, path: "/app/payment-verification" },
    { key: "nav.collections", icon: Receipt, path: "/app/collections" },
    { key: "nav.collectionIntelligence", icon: ShieldAlert, path: "/app/collection-intelligence" },
    { section: "Accounting" },
    { key: "nav.accDashboard", icon: BarChart3, path: "/app/accounting-dashboard" },
    { key: "nav.bankAccounts", icon: Building2, path: "/app/bank-accounts" },
    { key: "nav.cashAccounts", icon: Banknote, path: "/app/cash-accounts" },
    { key: "nav.journalEntries", icon: FileText, path: "/app/journal-entries" },
    { label: "Chart of Accounts", icon: BookOpen, path: "/app/accounting/chart-of-accounts" },
    { label: "Ledger", icon: BookOpen, path: "/app/accounting/ledger" },
    { label: "Profit & Loss", icon: TrendingUp, path: "/app/accounting/pnl" },
    { label: "Balance Sheet", icon: Receipt, path: "/app/accounting/balance-sheet" },
    { label: "Bank Statement", icon: CreditCard, path: "/app/accounting/bank-statement" },
    { label: "Exports", icon: FileText, path: "/app/accounting/exports" },
    { label: "Accounting Audit", icon: ShieldAlert, path: "/app/accounting/audit-logs" },
    { key: "nav.overdueWaivers", icon: Shield, path: "/app/overdue-waivers" },
    { key: "nav.customerLedger", icon: BookOpen, path: "/app/customer-ledger" },
    { key: "nav.interestOnlyAccounts", icon: PauseCircle, path: "/app/interest-only-accounts" },
    { key: "nav.partPaymentControls", icon: SplitSquareHorizontal, path: "/app/part-payment-controls" },
    { label: "Loan Portfolio", icon: BarChart3, path: "/app/portfolio" },
    { section: "Operations" },
    { key: "nav.paymentChannels", icon: CreditCard, path: "/app/payment-channels" },
    { key: "nav.analytics", icon: TrendingUp, path: "/app/analytics" },
    { key: "nav.agentPerformance", icon: TrendingUp, path: "/app/agent-performance" },
    { key: "nav.bulkImport", icon: Upload, path: "/app/bulk-import" },
    { key: "nav.importStaging", icon: FileSpreadsheet, path: "/app/import-batches" },
    { key: "nav.dataQuality", icon: ClipboardCheck, path: "/app/data-quality" },
    { section: "Commissions" },
    { key: "nav.commissionRules", icon: Settings, path: "/app/commission-settings" },
    { key: "nav.commissionLedger", icon: Award, path: "/app/commission-ledger" },
    { key: "nav.agentLedger", icon: Briefcase, path: "/app/agent-ledger" },
    { key: "nav.leaderboard", icon: Trophy, path: "/app/leaderboard" },
    { section: "System" },
    { key: "nav.settings", icon: Settings, path: "/app/settings" },
    { key: "nav.systemHealth", icon: Shield, path: "/app/system-health" },
    { key: "nav.duplicateResolution", icon: Fingerprint, path: "/app/duplicate-resolution" },
    { key: "nav.auditLogs", icon: FileText, path: "/app/audit" },
    { key: "nav.smsLogs", icon: MessageSquare, path: "/app/sms" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
  admin: [
    { key: "nav.dashboard", icon: LayoutDashboard, path: "/app/dashboard" },
    { key: "nav.users", icon: Users, path: "/app/users" },
    { key: "nav.customers", icon: UserCircle, path: "/app/customers" },
    { key: "nav.proposals", icon: ClipboardList, path: "/app/proposals" },
    { key: "nav.paymentVerification", icon: Wallet, path: "/app/payment-verification" },
    { key: "nav.collections", icon: Receipt, path: "/app/collections" },
    { key: "nav.collectionIntelligence", icon: ShieldAlert, path: "/app/collection-intelligence" },
    { key: "nav.agentPerformance", icon: TrendingUp, path: "/app/agent-performance" },
    { key: "nav.analytics", icon: BarChart3, path: "/app/analytics" },
    { key: "nav.bulkImport", icon: Upload, path: "/app/bulk-import" },
    { section: "Accounting" },
    { label: "Bank Accounts", icon: Building2, path: "/app/accounting/bank-accounts" },
    { label: "Journal Entries", icon: ClipboardList, path: "/app/accounting/journal-entries" },
    { label: "Chart of Accounts", icon: BookOpen, path: "/app/accounting/chart-of-accounts" },
    { label: "Ledger", icon: BookOpen, path: "/app/accounting/ledger" },
    { label: "Profit & Loss", icon: TrendingUp, path: "/app/accounting/pnl" },
    { label: "Balance Sheet", icon: Receipt, path: "/app/accounting/balance-sheet" },
    { label: "Bank Statement", icon: CreditCard, path: "/app/accounting/bank-statement" },
    { label: "Exports", icon: FileText, path: "/app/accounting/exports" },
    { key: "nav.customerLedger", icon: BookOpen, path: "/app/customer-ledger" },
    { label: "Loan Portfolio", icon: BarChart3, path: "/app/portfolio" },
    { section: "Commissions" },
    { key: "nav.commissionLedger", icon: Award, path: "/app/commission-ledger" },
    { key: "nav.agentLedger", icon: Briefcase, path: "/app/agent-ledger" },
    { key: "nav.leaderboard", icon: Trophy, path: "/app/leaderboard" },
    { section: "System" },
    { key: "nav.auditLogs", icon: FileText, path: "/app/audit" },
    { key: "nav.smsLogs", icon: MessageSquare, path: "/app/sms" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
  agent: [
    { key: "nav.dashboard", icon: LayoutDashboard, path: "/app/dashboard" },
    { key: "nav.myCustomers", icon: UserCircle, path: "/app/customers" },
    { key: "nav.proposals", icon: ClipboardList, path: "/app/proposals" },
    { key: "nav.collectionIntelligence", icon: ShieldAlert, path: "/app/collection-intelligence" },
    { key: "nav.bulkImport", icon: Upload, path: "/app/bulk-import" },
    { section: "Commissions" },
    { key: "nav.myCommissions", icon: Award, path: "/app/my-commissions" },
    { key: "nav.agentLedger", icon: Briefcase, path: "/app/agent-ledger" },
    { key: "nav.leaderboard", icon: Trophy, path: "/app/leaderboard" },
    { section: "" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
  customer: [
    { key: "nav.myLoans", icon: LayoutDashboard, path: "/app/customer-portal" },
    { key: "nav.emiSchedule", icon: Calendar, path: "/app/customer-portal/schedule" },
    { key: "nav.payEMI", icon: IndianRupee, path: "/app/customer-portal/pay" },
    { key: "nav.myPayments", icon: Receipt, path: "/app/customer-portal/my-payments" },
    { key: "nav.statements", icon: Download, path: "/app/customer-portal/statements" },
    { key: "nav.ledger", icon: BookOpen, path: "/app/customer-portal/ledger" },
    { key: "nav.training", icon: GraduationCap, path: "/app/training" },
  ],
};

const roleKeys = {
  superadmin: "role.superadmin", admin: "role.admin", agent: "role.agent", customer: "role.customer",
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, LANG_LABELS, LANG_FLAGS, languages } = useLanguage();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  // Track which sidebar sections are collapsed (default: all expanded)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem("sidebar.collapsedSections");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  const toggleSection = (name) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      try { localStorage.setItem("sidebar.collapsedSections", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const items = navKeys[user?.role] || [];
  const roleLabel = t(roleKeys[user?.role] || "role.customer");

  const roleBadgeColor = dark
    ? { superadmin: "bg-blue-500/20 text-blue-400", admin: "bg-purple-500/20 text-purple-400", agent: "bg-emerald-500/20 text-emerald-400", customer: "bg-amber-500/20 text-amber-400" }
    : { superadmin: "bg-blue-100 text-blue-700", admin: "bg-purple-100 text-purple-700", agent: "bg-emerald-100 text-emerald-700", customer: "bg-amber-100 text-amber-700" };

  const badge = roleBadgeColor[user?.role] || (dark ? "bg-slate-500/20 text-slate-400" : "bg-slate-100 text-slate-600");

  const navLinkClass = ({ isActive }) => {
    const base = "flex items-center gap-3 px-3 py-[9px] rounded-[10px] text-[13px] font-medium transition-all duration-200 group";
    if (isActive) {
      return dark
        ? `${base} bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-600/20`
        : `${base} bg-blue-50 text-blue-700 font-semibold`;
    }
    return dark
      ? `${base} text-slate-400 hover:bg-white/[0.05] hover:text-slate-200`
      : `${base} text-slate-500 hover:bg-slate-100 hover:text-slate-800`;
  };

  const sectionClass = dark
    ? "text-[10px] font-bold text-slate-500 uppercase tracking-[0.1em] px-3 pt-4 pb-1.5 mt-1"
    : "text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] px-3 pt-4 pb-1.5 mt-1";

  const borderStyle = { borderTop: `1px solid var(--fintech-border)` };

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex flex-col w-[240px] h-screen fixed left-0 top-0 z-40"
      style={{
        background: "var(--fintech-sidebar)",
        borderRight: `1px solid var(--fintech-border)`,
      }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5" style={{ borderBottom: `1px solid var(--fintech-border)` }}>
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/25">
          <span className="text-white font-bold text-sm">S</span>
        </div>
        <div>
          <p className="font-heading font-bold text-sm leading-none tracking-tight ft-text">{t("app.name")}</p>
          <p className="text-[11px] mt-0.5 ft-text-muted">{t("app.subtitle")}</p>
        </div>
      </div>

      {/* User card */}
      <div className="mx-3 mt-3 px-3 py-2.5 rounded-xl" style={{ background: dark ? "rgba(255,255,255,0.03)" : "var(--fintech-surface)", border: `1px solid var(--fintech-border-soft)` }}>
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <span className="text-white text-xs font-bold">{user?.full_name?.charAt(0)?.toUpperCase() || "U"}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold truncate ft-text">{user?.full_name}</p>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold ${badge}`}>{roleLabel}</span>
          </div>
        </div>
      </div>

      {/* Navigation — sections (Accounting, Operations, etc.) are collapsible */}
      <nav className="flex-1 px-3 pt-4 pb-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        {(() => {
          // Render items, grouping each section heading with its following items
          const out = [];
          let currentSection = null; // name of the open section, or null = top-level
          items.forEach((item, idx) => {
            if (item.section !== undefined) {
              currentSection = item.section || null;
              if (!currentSection) {
                out.push(<div key={`divider-${idx}`} className="pt-3 mt-1" style={borderStyle} />);
                return;
              }
              const sectionName = currentSection; // capture per-iteration for the click handler
              const isCollapsed = !!collapsedSections[sectionName];
              out.push(
                <button
                  key={`section-${idx}`}
                  type="button"
                  data-testid={`nav-section-toggle-${sectionName.toLowerCase()}`}
                  onClick={() => toggleSection(sectionName)}
                  className={`${sectionClass} w-full flex items-center justify-between hover:opacity-100 cursor-pointer`}
                  style={borderStyle}
                >
                  <span>{sectionName}</span>
                  {isCollapsed
                    ? <ChevronRight size={11} strokeWidth={2} className="opacity-70" />
                    : <ChevronDown size={11} strokeWidth={2} className="opacity-70" />}
                </button>
              );
              return;
            }
            // It's a nav item; hide if its section is collapsed
            if (currentSection && collapsedSections[currentSection]) return;
            const label = item.key ? t(item.key) : item.label;
            const testId = item.key
              ? `nav-${item.key.split(".")[1]}`
              : `nav-${item.path.split("/").pop()}`;
            out.push(
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/app/customer-portal"}
                data-testid={testId}
                className={navLinkClass}
              >
                <item.icon size={15} strokeWidth={1.75} className="flex-shrink-0 opacity-80 group-hover:opacity-100" />
                <span className="flex-1">{label}</span>
              </NavLink>
            );
          });
          return out;
        })()}
      </nav>

      {/* Bottom controls */}
      <div className="px-3 py-2" style={{ borderTop: `1px solid var(--fintech-border)` }}>
        <div className="flex items-center gap-1 mb-1">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${dark ? "text-slate-400 hover:text-slate-200 hover:bg-white/[0.05]" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`}
          >
            {dark ? <Sun size={13} /> : <Moon size={13} />}
            <span>{dark ? "Light" : "Dark"}</span>
          </button>

          <div className="relative ml-auto">
            <button
              data-testid="language-toggle"
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium ${dark ? "text-slate-400 hover:text-slate-200 hover:bg-white/[0.05]" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"}`}
            >
              <Globe size={13} />
              <span>{LANG_FLAGS[lang]}</span>
              <ChevronDown size={10} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute bottom-full left-0 w-40 mb-1 rounded-xl overflow-hidden z-50"
                style={{ background: "var(--fintech-card)", border: `1px solid var(--fintech-border)`, boxShadow: "var(--fintech-shadow-lg)" }}>
                {languages.map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-${l}`}
                    onClick={() => { setLang(l); setLangOpen(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                      l === lang
                        ? (dark ? "bg-blue-500/15 text-blue-400" : "bg-blue-50 text-blue-700") + " font-semibold"
                        : dark ? "text-slate-400 hover:bg-white/[0.03]" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span className="w-4 text-center">{LANG_FLAGS[l]}</span>
                    {LANG_LABELS[l]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1" style={{ borderTop: `1px solid var(--fintech-border)`, paddingTop: "6px" }}>
          <NavLink
            to="/app/change-password"
            data-testid="nav-change-password"
            className={({ isActive }) => {
              const base = "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all";
              if (isActive) return dark ? `${base} bg-blue-500/15 text-blue-400` : `${base} bg-blue-50 text-blue-700`;
              return dark ? `${base} text-slate-400 hover:text-slate-200 hover:bg-white/[0.05]` : `${base} text-slate-500 hover:text-slate-700 hover:bg-slate-100`;
            }}
          >
            <Lock size={12} />
            <span>Password</span>
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ml-auto ${dark ? "text-slate-400 hover:bg-red-500/10 hover:text-red-400" : "text-slate-500 hover:bg-red-50 hover:text-red-600"}`}
          >
            <LogOut size={12} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
